import React, { useEffect, useState } from "react";
import { Banner, Button, IndexTable, LegacyCard, Link, Page, Spinner, Text } from "@shopify/polaris";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import moment from "moment";
import SideMenu from "./SideMenu";
import { listCharges, resetChargesList } from "../../../redux/store/charges/Action";
import { findByIdApi } from "../../../redux/troubleshooting/clientDetails/Action";

const PAGE_SIZE = 10;
const PARTNER_ORG_ID = process.env.REACT_APP_SHOPIFY_PARTNER_ORG_ID;
const PARTNER_APP_ID = process.env.REACT_APP_SHOPIFY_PARTNER_APP_ID;

// Maps Partner API __typename to the URL segment used by the Partner Dashboard
// charges section. /charges/{category} (list) is used as the fallback; only
// subscription has a verified working detail page at /charges/{category}/{id}
// (usage detail 404s, one-time is unverified).
const CHARGE_CATEGORY_BY_TYPE = {
  AppSubscriptionSale: "subscription",
  AppOneTimeSale: "one_time",
  AppUsageSale: "usage",
};

const TYPES_WITH_DETAIL_URL = new Set(["AppSubscriptionSale"]);

const TYPE_LABELS = {
  AppSubscriptionSale: "Subscription",
  AppOneTimeSale: "One-time",
  AppUsageSale: "Usage",
  AppSaleAdjustment: "Refund",
};

const Charges = () => {
  const { id, storeType } = useParams();
  const dispatch = useDispatch();
  const chargesState = useSelector((store) => store.charges);
  const findClientsDetails = useSelector((store) => store.requriedFindDetails);
  const [transactions, setTransactions] = useState([]);
  const [endCursor, setEndCursor] = useState(null);
  const [hasNextPage, setHasNextPage] = useState(false);

  const shopExternalId = findClientsDetails?.data?.externalId;

  useEffect(() => {
    setTransactions([]);
    setEndCursor(null);
    setHasNextPage(false);
    dispatch(listCharges({ shopId: id, storeType, first: PAGE_SIZE }));
    dispatch(findByIdApi(id));
    return () => dispatch(resetChargesList());
  }, [dispatch, id, storeType]);

  useEffect(() => {
    if (chargesState.isSuccess && chargesState.data) {
      setTransactions((prev) => {
        const seen = new Set(prev.map((t) => t.id));
        const fresh = (chargesState.data.transactions || []).filter((t) => !seen.has(t.id));
        return [...prev, ...fresh];
      });
      setEndCursor(chargesState.data.endCursor || null);
      setHasNextPage(!!chargesState.data.hasNextPage);
    }
  }, [chargesState.isSuccess, chargesState.data]);

  const formatMoney = (m) =>
    m && m.amount ? `${parseFloat(m.amount).toFixed(2)} ${m.currencyCode || ""}` : "—";

  // Builds the Partner Dashboard URL for a transaction.
  //   Subscription → /charges/subscription/{chargeId}   (detail page works)
  //   Usage / One-time → /charges/{category}            (list; detail 404s for usage)
  //   No known category (refund) → shop overview URL
  const partnerDashboardUrl = (tx) => {
    if (!shopExternalId) return null;
    const category = CHARGE_CATEGORY_BY_TYPE[tx.type];
    if (!category) {
      return `https://partners.shopify.com/${PARTNER_ORG_ID}/stores/${shopExternalId}`;
    }
    const base = `https://partners.shopify.com/${PARTNER_ORG_ID}/apps/${PARTNER_APP_ID}/stores/${shopExternalId}/charges/${category}`;
    // chargeId comes back as a GID like "gid://shopify/AppSubscription/29337223423";
    // the Partner Dashboard URL needs just the trailing numeric ID.
    const numericChargeId = tx.chargeId ? tx.chargeId.split("/").pop() : null;
    if (TYPES_WITH_DETAIL_URL.has(tx.type) && numericChargeId) {
      return `${base}/${numericChargeId}`;
    }
    return base;
  };

  const loadMore = () => {
    if (endCursor) {
      dispatch(listCharges({ shopId: id, storeType, first: PAGE_SIZE, afterCursor: endCursor }));
    }
  };

  const rowMarkup = transactions.map((tx, index) => {
    const url = partnerDashboardUrl(tx);
    // Display the unique per-transaction ID (e.g. AppSubscriptionSale numeric tail) so
    // each row is distinguishable; the link still points to the parent charge via chargeId.
    const label = tx.id.split("/").pop();
    return (
    <IndexTable.Row id={tx.id} key={tx.id} position={index}>
      <IndexTable.Cell>
        {url ? <Link url={url} external>{label}</Link> : label}
      </IndexTable.Cell>
      <IndexTable.Cell>{moment(tx.createdAt).format("MMM DD, YYYY, h:mm A")}</IndexTable.Cell>
      <IndexTable.Cell>{TYPE_LABELS[tx.type] || tx.type}</IndexTable.Cell>
      <IndexTable.Cell>{formatMoney(tx.grossAmount)}</IndexTable.Cell>
      <IndexTable.Cell>{formatMoney(tx.netAmount)}</IndexTable.Cell>
    </IndexTable.Row>
    );
  });

  return (
    <Page fullWidth>
      <div className="maindiv" style={{ display: "flex", gap: "20px" }}>
        <SideMenu id={id} storeType={storeType} activeTab="charges" />
        <div className="abc" style={{ flex: 1 }}>
          <LegacyCard>
            <div style={{ padding: "20px" }}>
              <Text as="h2" variant="headingLg">Charges</Text>
            </div>
            {chargesState.isError && (
              <div style={{ padding: "0 20px 20px" }}>
                <Banner status="critical" title="Failed to load charges">
                  {chargesState.error}
                </Banner>
              </div>
            )}
            <IndexTable
              resourceName={{ singular: "charge", plural: "charges" }}
              itemCount={transactions.length}
              selectable={false}
              loading={chargesState.isRequest && transactions.length === 0}
              headings={[
                { title: "Charge ID" },
                { title: "Date" },
                { title: "Type" },
                { title: "Gross" },
                { title: "Net" },
              ]}
              emptyState={
                chargesState.isRequest ? (
                  <div style={{ padding: "40px", textAlign: "center" }}>
                    <Spinner accessibilityLabel="Loading charges" size="large" />
                  </div>
                ) : (
                  <div style={{ padding: "40px", textAlign: "center" }}>
                    No charges found for this store.
                  </div>
                )
              }
            >
              {rowMarkup}
            </IndexTable>
            {hasNextPage && (
              <div style={{ padding: "16px", textAlign: "center" }}>
                <Button onClick={loadMore} loading={chargesState.isRequest}>
                  Load more
                </Button>
              </div>
            )}
          </LegacyCard>
        </div>
      </div>
    </Page>
  );
};

export default Charges;
